import { Color, Spacing } from '@persona-id/design-tokens';
import { LoaderIcon } from '@persona-id/design-tokens/icons';
import { useCallback } from 'react';
import { FileRejection, useDropzone } from 'react-dropzone';
import Markdown from 'shared/components/Markdown';
import { MEDIA_QUERIES } from 'shared/styles/variables';
import styled, { css, keyframes } from 'styled-components';

import Spacer from 'design-system/components/Spacer';
import { BASE } from 'design-system/variables/spacing';
import { TransitionDuration } from 'design-system/variables/transitions';
import IconFile from 'images/icons/file.svg';
import IconCamera from 'images/icons/inquiry/camera.svg';
import Button, { ButtonType } from 'inquiry/components/Button';
import Dialog from 'inquiry/components/Dialog';
import AddButtonIcon from 'inquiry/components/ImageGallery/AddButton.svg';
import CloseButtonIcon from 'inquiry/components/ImageGallery/CloseButton.svg';
import ThemedParagraph from 'inquiry/components/ThemedParagraph';
import ThemedTitle from 'inquiry/components/ThemedTitle';
import useInputCapture from 'lib/hooks/useInputCapture';
import useToggle from 'lib/hooks/useToggle';
import { colorCss } from 'lib/inquiryStyles';
import { SerializedStepDocumentStyles } from 'lib/interfaces/StepsConfig';
import { assertNever } from 'lib/utils/types';
import ThemedSvg from 'verify/components/ThemedSvg';
import {
  DocumentCaptureOptionDesktop,
  DocumentCaptureOptionMobile,
} from 'verify/lib/interfaces/CaptureOption';

const ITEM_SIZE = 160;
// Only for small mobile devices
const ITEM_SIZE_SMALL = 120;
/**
 * Show 25% of row 3 to indicate scrollability
 * Added BASEs are for grid gaps
 */
const MIN_HEIGHT_MULTI_ROW = ITEM_SIZE * 2.25 + BASE * 3;
const MIN_HEIGHT_MULTI_ROW_SMALL = ITEM_SIZE_SMALL * 2.25 + BASE * 3;

const ImageGalleryContainer = styled.div<{ multiRow: boolean }>`
  display: grid;
  grid-gap: ${Spacing[3]};
  justify-content: center;

  grid-auto-flow: row;
  grid-auto-rows: ${ITEM_SIZE}px;
  grid-template-columns: repeat(2, ${ITEM_SIZE}px);

  overflow-x: auto;
  max-height: 50vh;
  ${({ multiRow }) => multiRow && `min-height: ${MIN_HEIGHT_MULTI_ROW}px`};

  @media ${MEDIA_QUERIES.isMobile} {
    max-height: 60vh;
  }

  @media ${MEDIA_QUERIES.isMobileSmall} {
    grid-auto-rows: ${ITEM_SIZE_SMALL}px;
    grid-template-columns: repeat(2, ${ITEM_SIZE_SMALL}px);
    max-height: 50vh;
    ${({ multiRow }) => multiRow && `min-height: ${MIN_HEIGHT_MULTI_ROW_SMALL}px`};
  }
`;

const ITEM_STYLE = css`
  height: ${ITEM_SIZE}px;
  width: ${ITEM_SIZE}px;
  border-radius: 4px;
  border: 1px solid ${Color.CoreSlate300};

  @media ${MEDIA_QUERIES.isMobileSmall} {
    height: ${ITEM_SIZE_SMALL}px;
    width: ${ITEM_SIZE_SMALL}px;
  }
`;

const AddImageContainer = styled.div<{
  isDragActive: boolean;
  isDragReject: boolean;
  disabled: boolean;
}>`
  ${ITEM_STYLE}
  ${({ disabled }) => {
    if (!disabled) {
      return css`
        :hover {
          background: ${Color.CoreSlate50};
        }
        :focus-within {
          background: ${Color.CoreSlate50};
        }
      `;
    }
  }}
  ${({ disabled, isDragActive, isDragReject }) => {
    if (disabled) {
      return;
    }
    if (isDragReject) {
      return css`
        border-color: ${Color.CoreRed400};
        background: ${Color.CoreRed100};
      `;
    } else if (isDragActive) {
      return css`
        background: ${Color.CoreSlate50};
      `;
    }
  }}
  transition: background ${TransitionDuration.Fast}, border-color ${TransitionDuration.Fast};
`;

const AddButton = styled.button<{
  disabled: boolean;
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  styles?: SerializedStepDocumentStyles['imagePreview']['plusIcon'];
}>`
  height: 100%;
  width: 100%;
  background: none;

  cursor: pointer;
  circle {
    fill: ${({ theme, styles }) => colorCss(styles?.fill.color) ?? theme.colorButtonPrimary};
  }
  path {
    stroke: ${({ styles }) => colorCss(styles?.stroke.color) ?? Color.PrimaryWhite};
  }

  // note that we will not support button states for this button with server side theming
  // since we don't actually need to use the disabled state for this anymore
  ${({ disabled }) => {
    if (disabled) {
      return css`
        cursor: default;
        circle {
          fill: ${Color.CoreSlate200};
        }

        path {
          stroke: #898d9e; /* Color not in design system */
        }
      `;
    }
  }}
`;

const PreviewImageContainer = styled.figure<{
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  styles?: SerializedStepDocumentStyles['imagePreview']['cropArea'];
}>`
  ${ITEM_STYLE}
  background-color: ${({ styles }) => colorCss(styles?.fill.color) ?? Color.CoreSlate50};
  position: relative;
  display: flex;
  flex-direction: column;
`;

/**
 * Does not use IconButton because the styles are quite different.
 *
 * NOTE: we will not support button states for this button with server side
 * theming since we don't actually need to use the disabled state for this
 * anymore.
 */
const RemoveButton = styled.button<{
  disabled: boolean;
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  styles?: SerializedStepDocumentStyles['imagePreview']['xIcon'];
}>`
  position: absolute;
  top: ${Spacing[2]};
  right: ${Spacing[2]};
  border-radius: 10px;
  height: 20px;
  width: 20px;
  cursor: pointer;
  transition:
    background ${TransitionDuration.Fast},
    box-shadow ${TransitionDuration.Fast};
  background: ${({ styles }) => colorCss(styles?.fill.color) ?? Color.PrimaryWhite};

  svg {
    color: ${({ styles }) => colorCss(styles?.stroke.color) ?? Color.CoreSlate700};
  }

  &:disabled {
    cursor: not-allowed;
    background: ${Color.CoreSlate200};
  }
  &:hover:not(:disabled) {
    background: ${Color.CoreSlate100};
  }
  &:focus-visible:not(:disabled) {
    box-shadow: 0 0 0 2px ${({ styles }) => colorCss(styles?.stroke.color) ?? Color.CoreSlate700};
  }
`;

const PreviewImageInner = styled.div`
  padding: ${Spacing[3]};
  flex: auto;
  /* Stop flex child from growing out of parent */
  min-height: 0;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const AspectRatioGuard = styled.div`
  height: 100%;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const Thumbnail = styled.img`
  height: auto;
  width: auto;
  max-height: 100%;
  max-width: 100%;
  image-orientation: from-image;
`;

const StyledThemedSvg = styled(ThemedSvg)`
  display: flex;
  align-items: center;
  justify-content: center;

  svg {
    height: 48px;
    width: 48px;
  }
`;

const Caption = styled.figcaption`
  height: 32px;
  width: 100%;
  flex: none;
  display: flex;
  align-items: center;
  background-color: ${Color.PrimaryWhite};
  border-bottom-left-radius: 4px;
  border-bottom-right-radius: 4px;
  padding: ${Spacing[2]} ${Spacing[3]};
`;

const CaptionText = styled(ThemedParagraph)`
  overflow-x: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
`;

function isPreviewableImage(fileType?: string): boolean {
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  return fileType?.includes('image') && fileType !== 'image/heic';
}

export const ACCEPTED_IMAGE_FORMATS = ['application/pdf', 'image/jpeg', 'image/png', 'image/heic'];

interface ImageGalleryFileBase {
  id: string;
  type: 'uploading' | 'uploaded';
  filename: string;
  fileType: string;
  url: string;
}

interface ImageGalleryFileUploading extends ImageGalleryFileBase {
  type: 'uploading';
  progress: number;
}

interface ImageGalleryFileUploaded extends ImageGalleryFileBase {
  type: 'uploaded';
  deleting?: boolean;
}

export type ImageGalleryFile = ImageGalleryFileUploading | ImageGalleryFileUploaded;

export interface ImageGalleryProps {
  /**
   * List of uploaded files. Should be stored in Redux to support routing to a
   * separate capture route.
   * FIXME: The values sent from the server will not be file objects.
   * Assuming they will just have filename, checksum, and maybe thumbnail.
   */
  files: ImageGalleryFile[]; // | CompletedUpload which is just a URL?
  /**
   * @params {File[]} newly added files
   */
  onChange: (files: File[]) => void;
  /**
   * Will only be triggered by 'upload'.
   */
  onAddError: (rejectedFiles: FileRejection[]) => void;
  onRemove: (fileId: string) => Promise<void>;
  /** Expected to route to a separate capture view, which will independently
   * update `files` via Redux.
   */
  onAddClick: () => void;
  onDeviceHandoff: () => void;
  acceptedImageFormats?: Readonly<string[]>;
  allowedCaptureOptions: Array<DocumentCaptureOptionDesktop | DocumentCaptureOptionMobile>;
  disabled?: boolean;
  addFileDisabled?: boolean;
  'data-test'?: string;
}

interface ImageGalleryLocalizations {
  captureOptionsDialogTitle: string;
  webCameraBtnLabel: string | undefined;
  mobileCameraBtnLabel: string | undefined;
  uploadBtnLabel: string;
  deviceHandoffBtnLabel: string;
  deleteDialogTitle: string;
  cancelBtnLabel: string;
  deleteBtnLabel: string;
  noCaptureOptionsPrompt: string;
  styles: SerializedStepDocumentStyles;
}

const ImageGallery = ({
  files,
  onChange,
  onRemove,
  onAddClick,
  onAddError,
  onDeviceHandoff,
  acceptedImageFormats = ACCEPTED_IMAGE_FORMATS,
  allowedCaptureOptions,
  disabled,
  addFileDisabled,
  'data-test': dataTest,
  captureOptionsDialogTitle,
  webCameraBtnLabel,
  mobileCameraBtnLabel,
  uploadBtnLabel,
  deviceHandoffBtnLabel,
  deleteDialogTitle,
  cancelBtnLabel,
  deleteBtnLabel,
  noCaptureOptionsPrompt,
  styles,
}: ImageGalleryProps & ImageGalleryLocalizations) => {
  const handleRemove = useCallback(
    async (removedIndex: number) => {
      // TODO: Can also improve this with optimistic updates.

      const removedFile = files[removedIndex];

      // TODO: implement removal of pending file? Axios interrupt token?
      if (removedFile.type === 'uploaded') {
        await onRemove(removedFile.id);
      }
    },
    [onRemove, files],
  );

  return (
    <>
      <ImageGalleryContainer data-test={dataTest} multiRow={files.length > 1}>
        {files.slice().map((file, index) => (
          <PreviewImage
            key={file.id}
            /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
            disabled={disabled}
            file={file}
            index={index}
            loading={false}
            onRemove={handleRemove}
            deleteDialogTitle={deleteDialogTitle}
            cancelBtnLabel={cancelBtnLabel}
            deleteBtnLabel={deleteBtnLabel}
            styles={styles}
          />
        ))}
        {addFileDisabled ? null : (
          <AddImage
            onAdd={onChange}
            onAddClick={onAddClick}
            onAddError={onAddError}
            onDeviceHandoff={onDeviceHandoff}
            acceptedImageFormats={acceptedImageFormats}
            allowedCaptureOptions={allowedCaptureOptions}
            /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
            disabled={disabled}
            captureOptionsDialogTitle={captureOptionsDialogTitle}
            webCameraBtnLabel={webCameraBtnLabel}
            mobileCameraBtnLabel={mobileCameraBtnLabel}
            uploadBtnLabel={uploadBtnLabel}
            deviceHandoffBtnLabel={deviceHandoffBtnLabel}
            noCaptureOptionsPrompt={noCaptureOptionsPrompt}
            styles={styles}
          />
        )}
      </ImageGalleryContainer>
    </>
  );
};

export default ImageGallery;

interface AddImageProps {
  onAdd: (acceptedFiles: File[]) => void;
  onAddClick: () => void;
  onAddError: (rejectedFiles: FileRejection[]) => void;
  onDeviceHandoff: () => void;
  disabled: boolean;
  acceptedImageFormats: Readonly<string[]>;
  allowedCaptureOptions: Array<DocumentCaptureOptionDesktop | DocumentCaptureOptionMobile>;
}
const AddImage = ({
  onAdd,
  onAddClick,
  onAddError,
  onDeviceHandoff,
  disabled,
  acceptedImageFormats,
  allowedCaptureOptions,
  captureOptionsDialogTitle,
  webCameraBtnLabel,
  mobileCameraBtnLabel,
  uploadBtnLabel,
  deviceHandoffBtnLabel,
  noCaptureOptionsPrompt,
  styles: { base, imagePreview, buttons },
}: AddImageProps &
  Pick<
    ImageGalleryLocalizations,
    | 'captureOptionsDialogTitle'
    | 'webCameraBtnLabel'
    | 'mobileCameraBtnLabel'
    | 'uploadBtnLabel'
    | 'deviceHandoffBtnLabel'
    | 'noCaptureOptionsPrompt'
    | 'styles'
  >) => {
  const [
    showCaptureOptionsDialog,
    { enable: openCaptureOptionsDialog, disable: hideCaptureOptionsDialog },
  ] = useToggle();

  const { getRootProps, getInputProps, isDragActive, isDragReject } = useDropzone({
    onDrop: (files) => {
      onAdd(files);
      hideCaptureOptionsDialog();
    },
    onDropRejected: onAddError,
    multiple: true,
    accept: acceptedImageFormats as string[],
  });

  const handleAddImageCamera = useCallback((file: File) => onAdd([file]), [onAdd]);

  const { inputCaptureProps, buttonProps } = useInputCapture({
    onSelectImage: (file) => {
      handleAddImageCamera(file);
      hideCaptureOptionsDialog();
    },
    capture: 'environment',
    disabled,
  });

  if (allowedCaptureOptions.length === 1) {
    switch (allowedCaptureOptions[0]) {
      case 'web_camera': {
        return (
          <AddImageContainer
            isDragActive={false}
            isDragReject={false}
            disabled={disabled}
            data-test='add-image__web-camera'
          >
            <AddButton
              id='add-image__add-btn'
              data-test='add-image__add-btn'
              aria-label='Add image'
              onClick={onAddClick}
              disabled={buttonProps.disabled}
              styles={imagePreview?.plusIcon}
            >
              <AddButtonIcon />
            </AddButton>
          </AddImageContainer>
        );
      }
      case 'mobile_camera': {
        return (
          <AddImageContainer
            isDragActive={false}
            isDragReject={false}
            disabled={disabled}
            data-test='add-image__mobile-camera'
          >
            <input {...inputCaptureProps} />
            <AddButton
              id='add-image__add-btn'
              data-test='add-image__add-btn'
              aria-label='Add image'
              styles={imagePreview?.plusIcon}
              {...buttonProps}
            >
              <AddButtonIcon />
            </AddButton>
          </AddImageContainer>
        );
      }
      case 'upload': {
        return (
          <AddImageContainer
            isDragActive={isDragActive}
            isDragReject={isDragReject}
            disabled={disabled}
            data-test='add-image__upload'
          >
            <AddButton
              id='add-image__add-btn'
              data-test='add-image__add-btn'
              aria-label='Add image'
              {...getRootProps()}
              disabled={disabled}
              styles={imagePreview?.plusIcon}
            >
              <input {...getInputProps()} data-cy='file-upload' />
              <AddButtonIcon />
            </AddButton>
          </AddImageContainer>
        );
      }
      case 'device_handoff': {
        return (
          <AddImageContainer
            isDragActive={false}
            isDragReject={false}
            disabled={disabled}
            data-test='add-image__device-handoff'
          >
            <AddButton
              id='add-image__add-btn'
              data-test='add-image__add-btn'
              aria-label='Add image'
              onClick={onDeviceHandoff}
              disabled={buttonProps.disabled}
              styles={imagePreview?.plusIcon}
            >
              <AddButtonIcon />
            </AddButton>
          </AddImageContainer>
        );
      }
      default: {
        assertNever(allowedCaptureOptions[0]);
      }
    }
  } else {
    // Will always return either web + upload or mobile + upload
    return (
      <>
        <Dialog
          isOpen={showCaptureOptionsDialog}
          onClose={hideCaptureOptionsDialog}
          data-test='capture-options-dialog'
          aria-labelledby='capture-options-dialog__title'
          aria-describedby='capture-options-dialog__description'
          styles={base?.modal}
        >
          <ThemedTitle styles={base?.titleStyle}>
            <Markdown>{captureOptionsDialogTitle}</Markdown>
          </ThemedTitle>

          <Spacer height={1} />

          {allowedCaptureOptions.length === 0 && (
            <ThemedParagraph styles={base?.textStyle}>
              <Markdown>{noCaptureOptionsPrompt}</Markdown>
            </ThemedParagraph>
          )}

          {allowedCaptureOptions.includes('web_camera') && (
            <>
              <Button
                id='add-image__dialog--btn-web-camera'
                data-test='add-image__dialog--btn-web-camera'
                buttonType={ButtonType.Primary}
                styles={buttons?.[ButtonType.Primary]}
                onClick={() => {
                  onAddClick();
                  hideCaptureOptionsDialog();
                }}
                disabled={disabled}
              >
                <CaptureButtonContent>{webCameraBtnLabel}</CaptureButtonContent>
              </Button>

              {/* Assume spacing is specified by styles if present */}
              {buttons == null && <Spacer height={1.5} />}
            </>
          )}

          {allowedCaptureOptions.includes('mobile_camera') && (
            <>
              <input {...inputCaptureProps} />
              <Button
                id='add-image__dialog--btn-mobile-camera'
                data-test='add-image__dialog--btn-mobile-camera'
                buttonType={ButtonType.Primary}
                styles={buttons?.[ButtonType.Primary]}
                /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                disabled={disabled}
                {...buttonProps}
              >
                <CaptureButtonContent>{mobileCameraBtnLabel}</CaptureButtonContent>
              </Button>

              {/* Assume spacing is specified by styles if present */}
              {buttons == null && <Spacer height={1.5} />}
            </>
          )}

          {allowedCaptureOptions.includes('upload') && (
            <>
              <Button
                id='add-image__dialog--btn-upload'
                data-test='add-image__dialog--btn-upload'
                buttonType={ButtonType.Secondary}
                styles={buttons?.[ButtonType.Secondary]}
                disabled={disabled}
                {...getRootProps()}
              >
                <input {...getInputProps()} data-cy='file-upload' />
                {uploadBtnLabel}
              </Button>

              {/* Assume spacing is specified by styles if present */}
              {buttons == null && <Spacer height={1.5} />}
            </>
          )}

          {allowedCaptureOptions.includes('device_handoff') && (
            <>
              <Button
                id='add-image__dialog--btn-device-handoff'
                data-test='add-image__dialog--btn-device-handoff'
                buttonType={ButtonType.Secondary}
                styles={buttons?.[ButtonType.Secondary]}
                onClick={() => {
                  onDeviceHandoff();
                  hideCaptureOptionsDialog();
                }}
                disabled={disabled}
              >
                {deviceHandoffBtnLabel}
              </Button>
            </>
          )}
        </Dialog>

        <AddImageContainer
          isDragActive={false}
          isDragReject={false}
          disabled={disabled}
          data-test='add-image--multiple-capture-options'
        >
          <AddButton
            id='add-image__add-btn'
            data-test='add-image__add-btn'
            aria-label='Add image'
            onClick={openCaptureOptionsDialog}
            disabled={buttonProps.disabled}
            styles={imagePreview?.plusIcon}
          >
            <AddButtonIcon />
          </AddButton>
        </AddImageContainer>
      </>
    );
  }
};

const LoadingContainer = styled.div`
  position: absolute;
  cursor: default;
  height: 100%;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

const LoadingBackdrop = styled.div`
  position: absolute;
  height: 100%;
  width: 100%;
  background: ${Color.PrimaryWhite};
  opacity: 0.8;
  border-radius: 4px;

  .fade-enter-done & {
    opacity: 0.8;
  }
  .fade-exit & {
    opacity: 0.8;
  }
`;

const rotating = keyframes`
  from {
    transform:  rotate(0deg);
  }
  to {
    transform:  rotate(360deg);
  }
`;

const AnimatedLoaderIcon = styled(LoaderIcon)`
  z-index: 1;
  margin: ${Spacing[2]};
  animation: ${rotating} 1s linear infinite;
`;

interface PreviewImageProps {
  disabled: boolean;
  file: ImageGalleryFile;
  index: number;
  loading: boolean;
  onRemove: (index: number) => Promise<void>;
}
const PreviewImage = ({
  file,
  disabled,
  index,
  loading,
  onRemove,
  deleteDialogTitle,
  deleteBtnLabel,
  cancelBtnLabel,
  styles: { base, imagePreview, buttons },
}: PreviewImageProps &
  Pick<
    ImageGalleryLocalizations,
    'deleteDialogTitle' | 'deleteBtnLabel' | 'cancelBtnLabel' | 'styles'
  >) => {
  const [showDeleteDialog, { enable: openDeleteDialog, disable: hideDeleteDialog }] = useToggle();

  const editable = !loading && file.type === 'uploaded';

  const handleRemove = useCallback(async () => {
    hideDeleteDialog();
    await onRemove(index);
  }, [onRemove, index]);

  const uploading = file.type === 'uploading' && file.progress < 100;
  const deleting = file.type === 'uploaded' && file.deleting;

  return (
    <>
      <Dialog
        isOpen={showDeleteDialog}
        onClose={hideDeleteDialog}
        data-test='delete-dialog'
        aria-labelledby='delete-dialog__title'
        aria-describedby='delete-dialog__description'
        styles={base?.modal}
      >
        <ThemedTitle styles={base?.titleStyle}>{deleteDialogTitle}</ThemedTitle>

        <Spacer height={1} />

        <Button
          buttonType={ButtonType.Primary}
          styles={buttons?.[ButtonType.Primary]}
          id='delete-dialog__btn-delete'
          data-test='delete-dialog__btn-delete'
          onClick={handleRemove}
        >
          {deleteBtnLabel}
        </Button>

        {/* Assume spacing is specified by styles if present */}
        {buttons == null && <Spacer height={1.5} />}

        <Button
          buttonType={ButtonType.Secondary}
          styles={buttons?.[ButtonType.Secondary]}
          id='delete-dialog__btn-cancel'
          data-test='delete-dialog__btn-cancel'
          onClick={hideDeleteDialog}
        >
          {cancelBtnLabel}
        </Button>
      </Dialog>
      <PreviewImageContainer styles={imagePreview?.cropArea}>
        {editable && !deleting && (
          <RemoveButton
            id='preview-image__remove-btn'
            data-test='preview-image__remove-btn'
            aria-label='Remove image'
            disabled={disabled}
            onClick={openDeleteDialog}
            styles={imagePreview?.xIcon}
          >
            <CloseButtonIcon />
          </RemoveButton>
        )}

        {(uploading || deleting) && (
          <LoadingContainer>
            <AnimatedLoaderIcon />
            <LoadingBackdrop />
          </LoadingContainer>
        )}

        <PreviewImageInner>
          {isPreviewableImage(file.fileType) ? (
            <AspectRatioGuard>
              <Thumbnail src={file.url} data-test='preview-image__thumbnail' />
            </AspectRatioGuard>
          ) : (
            <StyledThemedSvg data-test='preview-image__icon-file'>
              <IconFile />
            </StyledThemedSvg>
          )}
        </PreviewImageInner>

        <Caption title={file.filename} data-test='preview-image__caption'>
          <CaptionText styles={imagePreview?.imageName?.textStyle}>{file.filename}</CaptionText>
        </Caption>
      </PreviewImageContainer>
    </>
  );
};

const ImgCamera = styled(IconCamera)`
  vertical-align: bottom;

  path[stroke] {
    stroke: ${(props) => props.theme.colorFontButtonPrimary};
  }
`;

interface CaptureButtonContentProps {
  children: string | undefined;
}

const CaptureButtonContent = ({ children }: CaptureButtonContentProps) => {
  if (children) {
    return <>{children}</>;
  } else {
    return <ImgCamera data-test='government-id-prompt__image--camera' />;
  }
};
